import {Filter, FilterId} from "../../Models/Filter";
import {FilteringManagerWithContext} from "../../Filtering/FilteringManager";
import {FilterCheckWithContext} from "../../Filtering/FilterCheck";
import {
    SharedMaterialCoreData,
    SharedMaterialsCatalogCompany,
    StandardComplianceScoreType
} from "../../Models/SharedMaterials/DataModel";
import {RegulationFiterType} from "../../Models/SharedMaterials/BrowserStateModels";


export interface SharedMaterialFilterCheckContext {
    suppliersTable: { [supplierId: string]: SharedMaterialsCatalogCompany };
}

const UnrestrictedFilters = new Set<string>([
    FilterId.Category,
    FilterId.SharedMaterialsSearch,
    FilterId.SupplierId
]);

// ===============================================================================

/**
* Abstract class for all shared material filter checks. The passes check method for this
 * class makes sure the shared material object being passed in can be filtered on i.e. 
 * the current user has access to it or the active filter is an unrestricted filter.
*/
abstract class SharedMaterialsFilterCheck extends FilterCheckWithContext<SharedMaterialCoreData, SharedMaterialFilterCheckContext> {

    // ===============================================================================

    /**
    * To be overriden by subclasses. This method does all the logic for checking to 
    * see if a shared material passes a filter.
    * 
    * @param object the object to compare
    */
    protected abstract passesConcreteCheck(object: SharedMaterialCoreData): boolean;

    // ===============================================================================

    public passesCheck(object: SharedMaterialCoreData) {
        if (!object.hasAccess && !UnrestrictedFilters.has(this._activeFilter.filterId)) {
            return false;
        }

        return this.passesConcreteCheck(object);
    }
}

// ===============================================================================

/**
* Handles the search filter for the shared material browser.
*/
export class SharedMaterialsSearchFilterCheck extends SharedMaterialsFilterCheck {

    // ===============================================================================

    private _searchTerms: string[];

    // ===============================================================================

    constructor(activeFilter: Filter, context: SharedMaterialFilterCheckContext) {
        super(activeFilter, context);
        this._searchTerms = this._activeFilter.value.split(' ').map(s => s.trim().toLowerCase()).filter(s => s.length > 0);
    }

    // ===============================================================================

    /**
    * Gets the companay name to use for a shared material.
    */
    private getCompanyName(companyId: string, companies: { [companyId: string]: SharedMaterialsCatalogCompany }) {
        const company = companies[companyId] ?? null;
        const nameToUse = company?.name ?? "";

        return nameToUse.length === 0 ? "Unknown" : nameToUse;
    }

    // ===============================================================================

    /**
    * Gets a list of searchable strings for a shared material
    */
    private getSearchableStringsFromSharedMaterial(suppliersTable: { [id: string]: SharedMaterialsCatalogCompany }, sharedMaterial: SharedMaterialCoreData) {
        const ret = [
            sharedMaterial.materialName,
            this.getCompanyName(sharedMaterial.companyId, suppliersTable),
            sharedMaterial.sku
        ];

        for (let category of sharedMaterial.categories) {
            ret.push(category);
        }

        return ret.map(s => s.trim().toLowerCase());
    }

    // ===============================================================================

    protected passesConcreteCheck(object: SharedMaterialCoreData) {
        const searchableStringsForSharedMaterial = this.getSearchableStringsFromSharedMaterial(this._context.suppliersTable, object);
        return searchableStringsForSharedMaterial.some(s => this._searchTerms.every(st => s.indexOf(st) !== -1));
    }
}

// ===============================================================================

/**
* Handles a shared material regulation filter
*/
export class SharedMaterialsRegulationFilterCheck extends SharedMaterialsFilterCheck {

    // ===============================================================================

    /**
    * Returns true of false if a shared material passes a regulation filter.
    * 
    * @param regulationFilterType the regulation type we are checking the shared material against.
    * @param score the compliance score the shared material has for that regualation type
    */
    private passesRegulationFilter(regulationFilterType: RegulationFiterType, score: StandardComplianceScoreType | null) {
        const isCompliant = regulationFilterType === RegulationFiterType.Compliant

        return (
            // Restricted and no data will have a score of null. If the filter is not looking for compliant, we allow all shared materials that 
            // have a score of null or unknown to pass.
            (!isCompliant && (score !== StandardComplianceScoreType.Compliant && score !== StandardComplianceScoreType.NonCompliant)) ||
            (isCompliant && score === StandardComplianceScoreType.Compliant)
        );
    }

    // ===============================================================================

    /**
    * Based on the filter value as a regulations filter type, this method returns true of false if the shared materials passes that
    * regulation filter.
    */
    private applyRegulationFilter(activeFilter: Filter, sharedMaterial: SharedMaterialCoreData): boolean {
        const score = sharedMaterial.regulationIdToComplianceScoreMap[activeFilter.filterParameter ?? ""] ?? null;

        return this.passesRegulationFilter(activeFilter.value as RegulationFiterType, score);
    }

    // ===============================================================================

    protected passesConcreteCheck(object: SharedMaterialCoreData) {
        return this.applyRegulationFilter(this._activeFilter, object);
    }
}

// ===============================================================================

/**
* Handles a shared material category filter
*/
export class SharedMaterialsCategoryFilterCheck extends SharedMaterialsFilterCheck {

    // ===============================================================================

    protected passesConcreteCheck(object: SharedMaterialCoreData) {
        return object.categories.some(c => c.toLowerCase().trim() === this._activeFilter.value.toLowerCase().trim());
    }
}

// ===============================================================================

/**
* Handles a shared material sds filter
*/
export class SharedMaterialsSdsFilterCheck extends SharedMaterialsFilterCheck {

    // ===============================================================================

    protected passesConcreteCheck(object: SharedMaterialCoreData) {
        const valueToCompare = object.hasSdsStatus ? "yes" : "no";
        return valueToCompare.toLowerCase().trim() === this._activeFilter.value.toLowerCase().trim();
    }
}

// ===============================================================================

/**
* Handles a shared material supplier filter
*/
export class SharedMaterialsSupplierIdFilterCheck extends SharedMaterialsFilterCheck {

    // ===============================================================================

    protected passesConcreteCheck(object: SharedMaterialCoreData) {
        return object.companyId === this._activeFilter.value.toLowerCase().trim();
    }
}

// ===============================================================================

/**
* Concrete class for all shared materials filtering operations.
*/
export class SharedMaterialsFilteringManager extends FilteringManagerWithContext<SharedMaterialCoreData, SharedMaterialFilterCheckContext> {
    protected filterCheckFactory(activeFilter: Filter, context: SharedMaterialFilterCheckContext) {
        switch (activeFilter.filterId) {
            case FilterId.SharedMaterialsSearch:
                return new SharedMaterialsSearchFilterCheck(activeFilter, context);
            case FilterId.Category:
                return new SharedMaterialsCategoryFilterCheck(activeFilter, context);
            case FilterId.Sds:
                return new SharedMaterialsSdsFilterCheck(activeFilter, context);
            case FilterId.SupplierId:
                return new SharedMaterialsSupplierIdFilterCheck(activeFilter, context);
            case FilterId.SharedMaterialsCompliance:
                return new SharedMaterialsRegulationFilterCheck(activeFilter, context);
            default:
                throw new Error("Unknown Shared Material Filter Id: " + activeFilter.filterId);
        }
    }
}
